/**
 * Bolt chat composer — visual shell only.
 * Send, voice, and value stay owned by BoltChat.
 */

import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Send } from 'lucide-react';

const BOLT_PLACEHOLDERS = [
  'Ask Bolt about PPE for concrete work',
  'What are the fall protection requirements?',
  'Who is on site today?',
  'Summarize today’s hazards',
  'What permits are still open?',
];

const phraseVariants = {
  initial: { opacity: 0, y: 8 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] },
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function BoltChatComposer({
  inputRef,
  value,
  onChange,
  onKeyDown,
  onSend,
  disabled = false,
  sendDisabled = false,
  nativePlaceholder,
  voice,
}) {
  const wrapperRef = useRef(null);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const showCycler = !isActive && !value && !disabled;

  useEffect(() => {
    if (isActive || value || disabled) return undefined;
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % BOLT_PLACEHOLDERS.length);
    }, 3200);
    return () => clearInterval(interval);
  }, [disabled, isActive, value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        if (!value) setIsActive(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value]);

  useEffect(() => {
    const el = inputRef?.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 24), 120)}px`;
  }, [inputRef, value]);

  const activate = () => {
    if (disabled) return;
    setIsActive(true);
    inputRef?.current?.focus();
  };

  return (
    <div
      ref={wrapperRef}
      className={`bolt-chat-input bolt-chat-composer${isActive || value ? ' is-expanded' : ''}${showCycler ? ' is-idle' : ''}${value ? ' has-value' : ''}${disabled ? ' is-disabled' : ''}`}
      onClick={activate}
    >
      <div className="bolt-chat-composer-row">
        <div className="bolt-chat-composer-field">
          <textarea
            ref={inputRef}
            value={value}
            onChange={onChange}
            onKeyDown={onKeyDown}
            onFocus={() => setIsActive(true)}
            placeholder={nativePlaceholder}
            rows={1}
            disabled={disabled}
          />
          <div className="bolt-chat-composer-overlay" aria-hidden="true">
            <AnimatePresence mode="wait">
              {showCycler && (
                <motion.span
                  key={placeholderIndex}
                  className="bolt-chat-composer-cycle"
                  variants={phraseVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  {BOLT_PLACEHOLDERS[placeholderIndex]}
                </motion.span>
              )}
            </AnimatePresence>
          </div>
        </div>
        {voice}
        <button
          type="button"
          className="bolt-chat-send"
          title="Send"
          onClick={(event) => {
            event.stopPropagation();
            onSend();
          }}
          disabled={sendDisabled}
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}
